import { Link } from "react-router-dom";
import type { Comic } from "@/mocks/comics";
import { formatViews } from "@/lib/comics";

interface Props {
  comic: Comic;
  query?: string;
}

export default function ComicCard({ comic, query }: Props) {
  const href = `/detail/${comic.id}${query ? `?from=${encodeURIComponent(query)}` : ""}`;
  return (
    <article className="group card-lift rounded-lg overflow-hidden bg-background-900/70 border hairline flex flex-col">
      <Link to={href} className="block relative" aria-label={`查看《${comic.title}》详情`}>
        <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-800">
          <img
            src={comic.cover}
            alt={`《${comic.title}》漫画封面，${comic.categories.join("·")}题材，作者${comic.author}`}
            title={`${comic.title} 漫画封面`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/85 via-background-950/10 to-transparent" />
          <span className={`absolute top-2.5 left-2.5 h-6 px-2 rounded-md text-[11px] font-label flex items-center bg-background-950/70 border border-background-700/70 text-foreground-100 backdrop-blur-sm`}>
            {comic.status}
          </span>
          <span className="absolute top-2.5 right-2.5 h-6 px-2 rounded-md text-[11px] font-label flex items-center bg-accent-500 text-background-950">
            {comic.score.toFixed(1)}
          </span>
          <div className="absolute bottom-2.5 left-2.5 right-2.5 flex items-center justify-between text-[11px] text-foreground-200">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-accent-300" />
              {formatViews(comic.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-book-read-line text-secondary-300" />
              全 {comic.chapters} 话
            </span>
          </div>
        </div>
      </Link>
      <div className="p-3.5 md:p-4 flex flex-col gap-2 flex-1">
        <h3 className="text-sm md:text-[15px] font-medium text-foreground-50 leading-5 truncate">
          <Link to={href} className="hover:text-accent-300 transition-colors">
            {comic.title}
          </Link>
        </h3>
        <div className="flex items-center gap-2 text-[11px] text-foreground-400">
          <span className="truncate">{comic.author}</span>
          <span className="w-1 h-1 rounded-full bg-background-500 shrink-0" />
          <span className="whitespace-nowrap">{comic.region}</span>
        </div>
        <p className="text-xs leading-5 text-foreground-400 line-clamp-2">{comic.tagline}</p>
        <div className="mt-auto pt-2 flex items-center gap-1.5 flex-wrap">
          {comic.categories.map((cat) => (
            <span
              key={cat}
              className="h-5 px-1.5 rounded text-[10px] font-label flex items-center bg-secondary-900/60 text-secondary-200 border border-secondary-800/70"
            >
              {cat}
            </span>
          ))}
          <span className="ml-auto text-[10px] text-foreground-500 whitespace-nowrap">{comic.updatedAt.slice(5)} 更新</span>
        </div>
      </div>
    </article>
  );
}